// src/lib/dashboardGrouping.js
// ---------------------------------------------------------------------------
// Lógica del Centro de Comando: agrupación por cliente, motivo de bloqueo y
// avance de planchas. Pura y sin Firestore, para poder testearla.
//
// Los nombres de campo salen de AUDIT-DASHBOARD-UX-2026-08 §4, leídos de
// PRODUCCIÓN — no de los .md de estado. Lo que ese audit dice que NO existe
// (`archived`, `plannedDay`, contadores de planchas denormalizados en el job)
// aquí tampoco se inventa: el avance se calcula leyendo la subcolección.
// ---------------------------------------------------------------------------

import { repsOf, printedDone, cutOf, needsCut } from './production.js';
import { readinessLevel } from './productionFiles.js';
import { isActiveAlertTask, STATUS_DEVUELTO_DISENO } from '../utils/sheetStatus.js';

// Mismo criterio que `imprime()` en productionFiles.js y que `isTwoSided()` en
// production.js: el flag del documento, sin adivinar.
const imprime = (s) => !!s?.isPrinting;

/** Fases que NO cuentan como trabajo vivo. `status.phase`, igual que el resto del dashboard. */
export const CLOSED_PHASES = ['Finalizado', 'Pendiente por Facturar'];

/** ¿El job está finalizado? (51 de 62 en producción — por eso salen de la vista por default). */
export const isFinalizado = (job) => job?.status?.phase === 'Finalizado';

/** ¿Cuenta como trabajo activo para KPIs? Mismo criterio que el KPI "Trabajos Activos" de hoy. */
export const isActiveJob = (job) => !CLOSED_PHASES.includes(job?.status?.phase);

/**
 * Avance de producción de un job a partir de sus planchas.
 * Denominador = suma de `repetitions` (el audit confirma que no hay contador
 * denormalizado; hay que sumar). Solo cuentan al denominador las planchas que
 * efectivamente imprimen / cortan, para no inflarlo con las que no aplican.
 * @param {object[]} sheets
 * @return {{printDone:number, printTotal:number, cutDone:number, cutTotal:number, sheetCount:number}}
 */
export function sheetProgress(sheets = []) {
    const acc = { printDone: 0, printTotal: 0, cutDone: 0, cutTotal: 0, sheetCount: 0 };
    (sheets || []).forEach((s) => {
        const reps = repsOf(s);
        acc.sheetCount++;
        if (imprime(s)) {
            acc.printTotal += reps;
            acc.printDone += Math.min(printedDone(s), reps);
        }
        if (needsCut(s)) {
            acc.cutTotal += reps;
            acc.cutDone += Math.min(cutOf(s), reps);
        }
    });
    return acc;
}

/** Tareas abiertas del job (cualquier tipo). `status !== 'completed'`, igual que JobView. */
export const openTasks = (tasks = []) => (tasks || []).filter((t) => t?.status !== 'completed');

/**
 * Motivos de bloqueo de un job. Devuelve [] si no hay ninguno.
 *
 * Cada motivo sale de un campo VERIFICADO en producción (audit §4):
 *   hold     · `status.phase === 'En Hold'`
 *   alerta   · plancha con `hasActiveAlert === true`, o tarea pendiente de tipo
 *              `system` / `system_reproduction` (ALERT_TASK_TYPES)
 *   devuelta · plancha con `status`/`prepressStatus` en "Devuelto a Diseño"
 *   archivos · plancha en ROJO de `readinessLevel` — le falta un archivo requerido
 *              Y ya está en "Listo para Producción". Se usa el rojo y NO el ámbar
 *              a propósito: el ámbar es 9.6% de las planchas (audit §1 de archivos)
 *              y convertiría el KPI en ruido; el ámbar se muestra como contexto.
 *
 *   material · algún material del job tiene demanda > `stock.quantity`. La regla
 *              y el shape se verificaron contra producción el 2026-09-02
 *              (`stock.quantity` es number en 47/47). Sin ese dato NO bloquea.
 *
 * @param {object} job
 * @param {{sheets?:object[], tasks?:object[]}} enrich  datos de subcolecciones (pueden faltar)
 * @param {{shortages?:Record<string,{name:string,demanda:number,stock:number}>}} [opts]
 * @return {{kind:string, label:string}[]}
 */
export function jobBlocks(job, enrich = {}, opts = {}) {
    const sheets = enrich.sheets || [];
    const tasks = enrich.tasks || [];
    const out = [];

    if (job?.status?.phase === 'En Hold') {
        out.push({ kind: 'hold', label: 'En Hold' });
    }

    const alertSheets = sheets.filter((s) => s?.hasActiveAlert === true).length;
    const alertTasks = tasks.filter(isActiveAlertTask).length;
    if (alertSheets || alertTasks) {
        const n = Math.max(alertSheets, alertTasks);
        out.push({ kind: 'alert', label: `Alerta activa · ${n} plancha${n === 1 ? '' : 's'}` });
    }

    const devueltas = sheets.filter(
        (s) => s?.status === STATUS_DEVUELTO_DISENO || s?.prepressStatus === STATUS_DEVUELTO_DISENO,
    ).length;
    if (devueltas) {
        out.push({ kind: 'returned', label: `${devueltas} devuelta${devueltas === 1 ? '' : 's'} a diseño` });
    }

    const enRojo = sheets.filter((s) => readinessLevel(s) === 'error').length;
    if (enRojo) {
        out.push({ kind: 'files', label: `${enRojo} en producción sin archivo requerido` });
    }

    jobMaterialShortages(enrich, opts.shortages || {}).forEach((m) => {
        out.push({ kind: `material:${m.materialId}`, label: `${m.name} · faltan ${m.demanda - m.stock}` });
    });

    return out;
}

/** Planchas incompletas que todavía están en prepress (ámbar): contexto, no bloqueo. */
export const pendingFileCount = (sheets = []) =>
    (sheets || []).filter((s) => readinessLevel(s) === 'falta').length;

/** ¿El job está bloqueado? Solo es fiable si ya se cargaron sus subcolecciones. */
export const isJobBlocked = (job, enrich = {}, opts = {}) => jobBlocks(job, enrich, opts).length > 0;

/**
 * Agrupa jobs por cliente, ordenando: primero el grupo con más bloqueados, y
 * dentro de cada grupo el orden que ya traía la lista (el sort de la tabla).
 * Los jobs sin cliente caen en un grupo propio al final.
 *
 * @param {object[]} jobs
 * @param {(clientId:string)=>string} getClientName
 * @param {Record<string,{sheets?:object[],tasks?:object[]}>} enrichById
 * @param {{shortages?:object}} [opts]
 * @return {{clientId:string, clientName:string, jobs:object[], blockedCount:number, noDate:number, pendingFiles:number}[]}
 */
export function groupJobsByClient(jobs = [], getClientName = () => 'N/A', enrichById = {}, opts = {}) {
    const byClient = new Map();
    (jobs || []).forEach((job) => {
        const key = job?.clientId || '__sin_cliente__';
        if (!byClient.has(key)) {
            byClient.set(key, {
                clientId: key,
                clientName: job?.clientId ? getClientName(job.clientId) : 'Sin cliente',
                jobs: [],
                blockedCount: 0,
                noDate: 0,
                pendingFiles: 0,
            });
        }
        const g = byClient.get(key);
        const e = enrichById[job.id] || {};
        g.jobs.push(job);
        if (isJobBlocked(job, e, opts)) g.blockedCount++;
        if (!job?.deliveryDate) g.noDate++;
        g.pendingFiles += pendingFileCount(e.sheets);
    });

    return [...byClient.values()].sort((a, b) => {
        if (a.clientId === '__sin_cliente__') return 1;
        if (b.clientId === '__sin_cliente__') return -1;
        if (b.blockedCount !== a.blockedCount) return b.blockedCount - a.blockedCount;
        return a.clientName.localeCompare(b.clientName);
    });
}

/**
 * Días hasta la entrega. `deliveryDate` es string 'YYYY-MM-DD' o '' (audit §4:
 * nunca Timestamp). Devuelve null si no hay fecha.
 * @param {string} deliveryDate
 * @param {Date} [hoy]
 * @return {number|null}
 */
export function daysToDelivery(deliveryDate, hoy = new Date()) {
    if (!deliveryDate || typeof deliveryDate !== 'string') return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(deliveryDate.trim());
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (Number.isNaN(d.getTime())) return null;
    const base = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    return Math.round((d - base) / 86400000);
}

// ─── Ronda 2 (2026-09-02) ───────────────────────────────────────────────────

/**
 * Demanda de material de los jobs vivos: Σ(repetitions − printedCount) por
 * `materialId`. Solo suma lo que falta por imprimir; una plancha ya impresa no
 * consume stock otra vez. Los negativos (contador por encima de reps) se ignoran.
 *
 * @param {Record<string,{sheets?:object[]}>} enrichById  enriquecimiento por job
 * @param {string[]} jobIds  jobs a considerar (los vivos)
 * @return {Record<string, number>} materialId → planchas pendientes
 */
export function materialDemand(enrichById = {}, jobIds = []) {
    const demanda = {};
    jobIds.forEach((jobId) => {
        (enrichById[jobId]?.sheets || []).forEach((s) => {
            if (!s?.materialId) return;
            const pend = (Number(s.repetitions) || 0) - (Number(s.printedCount) || 0);
            if (pend > 0) demanda[s.materialId] = (demanda[s.materialId] || 0) + pend;
        });
    });
    return demanda;
}

/**
 * Materiales cuya demanda supera el stock. Verificado contra producción
 * (2026-09-02): `stock.quantity` es number en 47/47 materiales. Aun así, si un
 * doc NO trae `stock.quantity` numérico **no bloquea** — decisión de Daniel: sin
 * dato no se inventa una falta.
 *
 * @param {Record<string,number>} demanda
 * @param {object[]} materials  docs de `materials`
 * @return {Record<string, {name:string, demanda:number, stock:number}>}
 */
export function materialShortages(demanda = {}, materials = []) {
    const byId = new Map((materials || []).map((m) => [m.id, m]));
    const out = {};
    Object.entries(demanda).forEach(([materialId, dem]) => {
        const m = byId.get(materialId);
        const q = m?.stock?.quantity;
        if (typeof q !== 'number') return;          // sin dato → no bloquea
        if (dem > q) out[materialId] = { name: m.name || materialId, demanda: dem, stock: q };
    });
    return out;
}

/** Materiales en falta que USA este job concreto. */
export function jobMaterialShortages(enrich = {}, shortages = {}) {
    const usados = new Set((enrich.sheets || []).map((s) => s?.materialId).filter(Boolean));
    return Object.entries(shortages)
        .filter(([materialId]) => usados.has(materialId))
        .map(([materialId, info]) => ({ materialId, ...info }));
}

// Nombres legibles de los eventos de `jobs/{id}/ai_logs` (shape verificado en
// producción: { event, details, user, timestamp }). El texto útil vive en
// `details.titulo`; AI_SUMMARY_GENERATED solo trae `details.context`, que es el
// prompt completo — por eso NUNCA se usa como texto de la nota.
const EVENT_LABEL = {
    TAREA_CREADA: 'Tarea creada',
    TAREA_COMPLETADA: 'Tarea completada',
    TAREA_EDITADA: 'Tarea editada',
    AI_SUMMARY_GENERATED: 'Resumen IA generado',
    CAMBIO_ESTADO_PREPRESS: 'Cambio de estado en prepress',
};

const MES3 = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** Fecha corta '01-sep'. */
export function shortDate(date) {
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) return '';
    return `${String(date.getDate()).padStart(2, '0')}-${MES3[date.getMonth()]}`;
}

/**
 * Resumen del último evento del job para la columna Notas.
 * @param {object} log  doc de ai_logs (o null)
 * @param {number} [max]  largo máximo del texto
 * @return {{date:string, text:string, full:string}|null}
 */
export function lastLogSummary(log, max = 40) {
    if (!log) return null;
    const d = log.details || {};
    const crudo = d.titulo || d.title || d.descripcion || d.description
        || EVENT_LABEL[log.event] || String(log.event || '').replace(/_/g, ' ').toLowerCase();
    const full = String(crudo).replace(/\s+/g, ' ').trim();
    const text = full.length > max ? `${full.slice(0, max - 1)}…` : full;
    const date = shortDate(log.timestamp?.toDate ? log.timestamp.toDate() : log.timestamp);
    return { date, text, full };
}
